import { useCallback, useEffect, useRef, useState } from "react";
import { pingCloudChauffeur } from "../../../../services/serversService";
import triggerOnPremRemoteCommand from "../../../../services/remoteControlService";

/**
 * Probes whether a server hostname is reachable.
 *
 * Returns:
 *   - `status`:  "idle" | "pinging" | "reachable" | "unreachable" | "error"
 *   - `ping(hostname, mode)`: returns a Promise resolving to the reachability
 *     boolean, or `null` if the call was superseded by a later ping.
 *   - `reset()`: clears status back to idle.
 *
 * Each `ping` invalidates the previous one via a monotonic epoch, so a
 * slow earlier ping cannot overwrite the displayed state.
 */
export default function useServerReachability() {
  const [status, setStatus] = useState("idle");
  const epochRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reset = useCallback(() => {
    epochRef.current += 1;
    if (mountedRef.current) setStatus("idle");
  }, []);

  const ping = useCallback(
    async (hostname, mode) => {
      if (!hostname) {
        reset();
        return null;
      }
      epochRef.current += 1;
      const epoch = epochRef.current;
      if (mountedRef.current) setStatus("pinging");

      const stillLatest = () => mountedRef.current && epoch === epochRef.current;

      try {
        if (mode === "cloud") {
          const { data } = await pingCloudChauffeur(hostname);
          const isReachable = data?.status === "OK";
          if (!stillLatest()) return null;
          setStatus(isReachable ? "reachable" : "unreachable");
          return isReachable;
        }
        const response = await triggerOnPremRemoteCommand({
          hostname,
          cmd: "file",
          cmdArgs: { path: "/etc/hostname" },
          timeout: 5,
        });
        const isReachable = response?.status === "OK";
        if (!stillLatest()) return null;
        setStatus(isReachable ? "reachable" : "unreachable");
        return isReachable;
      } catch {
        if (!stillLatest()) return null;
        setStatus("error");
        return false;
      }
    },
    [reset]
  );

  return { status, ping, reset };
}
